import offlineStorage from './OfflineStorage';
import onlineDetector from './OnlineDetector';
import { ENTITY_API_MAP, ENTITY_STORES, SyncQueueStore } from './db';
import { API_BASE_URL } from '../utils/constants';
import { showToast } from '../utils/toast';
import logger from '../utils/logger';

const BASE = API_BASE_URL.replace(/\/$/, '');

function _entityTypeFromUrl(url) {
  const path = typeof url === 'string' ? url : '';
  const urlPath = path.startsWith(BASE) ? path.slice(BASE.length) : path;
  for (const [entity, apiPath] of Object.entries(ENTITY_API_MAP)) {
    const fullPath = '/' + apiPath;
    if (urlPath === fullPath || urlPath.startsWith(fullPath + '/')) {
      return entity;
    }
  }
  return null;
}

function _extractServerId(url) {
  const path = typeof url === 'string' ? url : '';
  const urlPath = path.startsWith(BASE) ? path.slice(BASE.length) : path;
  const parts = urlPath.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (parts.length >= 2) {
    const id = parts[1];
    if (!id) return null;
    try {
      return decodeURIComponent(id);
    } catch {
      return id;
    }
  }
  return null;
}

function _generateId() {
  return 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

async function _saveToCache(entityType, record) {
  const store = ENTITY_STORES[entityType];
  if (!store) return;
  const owner = offlineStorage.getCurrentOwner();
  if (!owner) return;
  const serverId = record._id || record.id;
  if (!serverId) return;
  const existing = await offlineStorage.getByServerId(entityType, serverId);
  const localId = existing?.localId || serverId;
  await store.put({
    localId,
    owner,
    data: { ...record },
    version: record.version || 1,
    serverId,
    createdAt: existing?.createdAt || Date.now(),
    updatedAt: Date.now(),
    syncedAt: Date.now(),
  });
}

const originalFetch = window.fetch.bind(window);

let _installed = false;

export function installFetchInterceptor() {
  if (_installed) return;
  _installed = true;

  window.fetch = async function interceptedFetch(input, init = {}) {
    // Always include credentials for cross-origin cookie support (unless explicitly set)
    if (!init.credentials) {
      init = { ...init, credentials: 'include' };
    }
    const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : '');
    const method = (init.method || (input instanceof Request ? input.method : 'GET') || 'GET').toUpperCase();
    const entityType = _entityTypeFromUrl(url);
    const serverId = _extractServerId(url);

    if (!entityType) {
      return originalFetch(input, init);
    }

    const isOnline = onlineDetector.isOnline;

    // ---- GET ----
    if (method === 'GET') {
      try {
        if (isOnline) {
          const response = await originalFetch(input, init);
          if (response.ok) {
            const clone = response.clone();
            const data = await clone.json();
            if (Array.isArray(data)) {
              offlineStorage.cacheServerData(entityType, data).catch(() => {});
            }
          }
          return response;
        }
      } catch {}
      const records = await offlineStorage.getAll(entityType);
      const data = records.map(r => r.data);
      return new Response(JSON.stringify(data), {
        status: 200,
        headers: { 'Content-Type': 'application/json', 'X-Offline-Source': 'indexeddb' },
      });
    }

    // ---- POST (Create) ----
    if (method === 'POST') {
      let body = {};
      try {
        body = init.body ? JSON.parse(init.body) : {};
      } catch {}

      if (isOnline) {
        try {
          const response = await originalFetch(input, init);
          if (response.ok) {
            const serverData = await response.clone().json();
            await _saveToCache(entityType, serverData);
          }
          return response;
        } catch (err) {
          const isNetworkErr = !navigator.onLine || err.message === 'Network Error' || err.name === 'TypeError' || err.name === 'AbortError';
          if (!isNetworkErr) throw err;
        }
      }

      const localId = _generateId();
      const now = Date.now();
      const owner = offlineStorage.getCurrentOwner();

      const store = ENTITY_STORES[entityType];
      if (store && owner) {
        const record = {
          localId,
          owner,
          data: { ...body, localId },
          version: 1,
          serverId: null,
          createdAt: now,
          updatedAt: now,
          syncedAt: null,
        };
        await store.put(record);
        await SyncQueueStore.add({
          localId, entityType, action: 'create',
          data: record.data, version: 1, owner,
          status: 'pending', retryCount: 0, createdAt: now,
        });
      }
      showToast('Saved offline — will sync when connected', { type: 'info', duration: 4000 });
      return new Response(JSON.stringify({ ...body, localId, _offline: true }), {
        status: 201,
        headers: { 'Content-Type': 'application/json', 'X-Offline-Source': 'indexeddb' },
      });
    }

    // ---- PUT / PATCH (Update) ----
    if (method === 'PUT' || method === 'PATCH') {
      let body = {};
      try { body = init.body ? JSON.parse(init.body) : {}; } catch {}

      if (isOnline) {
        try {
          const response = await originalFetch(input, init);
          if (response.ok) {
            const serverData = await response.clone().json();
            await _saveToCache(entityType, serverData);
          }
          return response;
        } catch (err) {
          const isNetworkErr = !navigator.onLine || err.message === 'Network Error' || err.name === 'TypeError' || err.name === 'AbortError';
          if (!isNetworkErr) throw err;
        }
      }

      // Offline — editing/updating is blocked
      showToast('Editing is not available while offline. Please reconnect to make changes.', { type: 'error', duration: 5000 });
      return new Response(JSON.stringify({ error: 'Offline - edits not allowed', _offline: true }), {
        status: 503,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    // ---- DELETE ----
    if (method === 'DELETE') {
      const owner = offlineStorage.getCurrentOwner();

      if (isOnline) {
        try {
          const response = await originalFetch(input, init);
          if (response.ok) {
            if (serverId) {
              const existing = await offlineStorage.getByServerId(entityType, serverId);
              if (existing) {
                const store = ENTITY_STORES[entityType];
                await store.delete(existing.localId);
              }
              const allQueue = await SyncQueueStore.getAll();
              for (const q of allQueue) {
                if (q.owner === owner && q.localId === serverId) {
                  await SyncQueueStore.delete(q.id);
                }
              }
            }
          }
          return response;
        } catch (err) {
          const isNetworkErr = !navigator.onLine || err.message === 'Network Error' || err.name === 'TypeError' || err.name === 'AbortError';
          if (!isNetworkErr) throw err;
        }
      }

      // Offline — deleting is blocked
      showToast('Deleting is not available while offline. Please reconnect to delete.', { type: 'error', duration: 5000 });
      return new Response(JSON.stringify({ error: 'Offline - deletes not allowed', _offline: true }), {
        status: 503,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    return originalFetch(input, init);
  };

  logger.info('[FetchInterceptor] Installed', null, 'Offline');
}

export function uninstallFetchInterceptor() {
  if (_installed) {
    window.fetch = originalFetch;
    _installed = false;
    logger.info('[FetchInterceptor] Uninstalled', null, 'Offline');
  }
}
